import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { useProductViewModel } from "../../../../viewmodels/website/useProductViewModel";
import { API_BASE_IMAGE_URL } from "../../../../config/apiConfig";

interface Props {
  isOpen: boolean;
  productId: string;
  onClose: () => void;
}

const ProductViewModal = ({ isOpen, productId, onClose }: Props) => {
  const { getProductById } = useProductViewModel();

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<any>(null);

  useEffect(() => {
    if (!isOpen || !productId) return;

    const load = async () => {
      setLoading(true);

      try {
        const res = await getProductById(productId);
        setProduct(res);
      } catch (error) {
        console.error("Failed to load product:", error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, productId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5">
      <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold">Product Details</h2>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="p-10 text-center text-slate-500">
            Loading...
          </div>
        ) : product ? (
          <div className="p-6 space-y-8">

            {/* Product Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                Product Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                <Field
                  title="Product Name"
                  value={product.name}
                />

                <Field
                  title="Model Number"
                  value={product.modelNumber}
                />

                <Field
                  title="Category"
                  value={product.categoryName}
                />

                <Field
                  title="Sub Category"
                  value={product.subCategoryName}
                />

                <Field
                  title="Capacity"
                  value={
                    product.capacity !== null &&
                    product.capacity !== undefined
                      ? `${product.capacity} ${product.unit || ""}`.trim()
                      : "-"
                  }
                />

                <Field
                  title="Capacity Text"
                  value={product.capacityText}
                />

                <Field
                  title="Division"
                  value={product.division}
                />

                <Field
                  title="Division Text"
                  value={product.divisionText}
                />

                <Field
                  title="Platform Size"
                  value={product.platformSize}
                />

                <Field
                  title="Material"
                  value={product.material}
                />

                <Field
                  title="Display Type"
                  value={product.displayType}
                />

                <Field
                  title="Power Supply"
                  value={product.powerSupply}
                />

                <Field
                  title="Communication"
                  value={product.communication}
                />

                <Field
                  title="Ingress Protection"
                  value={product.ingressProtection}
                />

                <Field
                  title="Operating Temperature"
                  value={product.operatingTemp}
                />

                <Field
                  title="Warranty"
                  value={product.warranty}
                />

              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                Description
              </h3>

              <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 whitespace-pre-wrap">
                {product.description || "-"}
              </div>
            </div>

            {/* Images */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                Product Images
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">

                <ImageCard
                  title="Front"
                  src={product.frontImageUrl}
                />

                <ImageCard
                  title="Back"
                  src={product.backImageUrl}
                />

                <ImageCard
                  title="Left"
                  src={product.leftImageUrl}
                />

                <ImageCard
                  title="Right"
                  src={product.rightImageUrl}
                />

              </div>
            </div>

            {/* Attachment */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                Attachment
              </h3>

              {product.attachment ? (
                <a
                  href={`${API_BASE_IMAGE_URL}/${product.attachment}`.replace(
                    /([^:]\/)\/+/g,
                    "$1",
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  📄 Download Attachment
                </a>
              ) : (
                <p className="text-slate-500">
                  No attachment uploaded.
                </p>
              )}
            </div>

          </div>
        ) : (
          <div className="p-10 text-center text-slate-500">
            Product not found.
          </div>
        )}

      </div>
    </div>
  );
};

const Field = ({
  title,
  value,
}: {
  title: string;
  value?: string | number | null;
}) => (
  <div className="border border-slate-200 rounded-xl p-4 bg-white">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
      {title}
    </p>

    <p className="text-base font-medium text-slate-800 break-words">
      {value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
        ? value
        : "-"}
    </p>
  </div>
);

const ImageCard = ({
  title,
  src,
}: {
  title: string;
  src?: string;
}) => {
  if (!src) return null;

  const imageUrl = src.startsWith("http")
    ? src
    : `${API_BASE_IMAGE_URL}/${src}`.replace(
        /([^:]\/)\/+/g,
        "$1",
      );

  return (
    <div>
      <div className="font-medium mb-2">
        {title}
      </div>

      <img
        src={imageUrl}
        alt={title}
        className="w-full h-44 object-cover rounded-xl border border-slate-200"
        onError={(e) => {
          e.currentTarget.src = "/images/no-image.png";
        }}
      />
    </div>
  );
};

export default ProductViewModal;